/**
 * WikiSync - Background Synchronization for Wiki Calculator Data
 *
 * Manages fetching updates from Firebase and updating the IndexedDB cache.
 * Operates silently in the background to keep cache in sync.
 *
 * Sync Strategy:
 * 1. On page load: Check if cache is stale (>5 min old)
 * 2. If stale: Fetch updates in background (doesn't block UI)
 * 3. If cache empty: Fetch initial data (waits for completion)
 * 4. Periodic: Every 5 minutes, silently check for updates
 *
 * Usage:
 *   const sync = new WikiSync(wikiDB);
 *   await sync.syncIfNeeded();  // On page load
 *   sync.startBackgroundSync(); // Periodic updates
 *
 * ENDPOINT: https://instacalc.com/api/wiki/listRecent
 */

const wikiSyncBuildTime = document.lastModified;
console.log('[WikiSync] Module loaded - Last modified:', wikiSyncBuildTime, '- Using https://instacalc.com/api/wiki');

class WikiSync {
  constructor(wikiDB) {
    this.wikiDB = wikiDB;
    this.syncing = false;
    this.syncInterval = null;
    this.syncIntervalMs = 300000; // 5 minutes
    // Use live site for calculator data
    this.firebaseEndpoint = 'https://instacalc.com/api/wiki';
    this.buildTime = wikiSyncBuildTime;
  }

  /**
   * Sync on page load if cache is stale
   * - If cache is empty, waits for initial fetch
   * - If cache exists but is stale, updates in background
   */
  async syncIfNeeded() {
    try {
      console.log('[WikiSync] syncIfNeeded() called');

      const count = await this.wikiDB.getCount();
      console.log('[WikiSync] Cache has', count, 'calculators');

      // If cache is empty, fetch immediately (blocking)
      if (count === 0) {
        console.log('[WikiSync] Cache empty, fetching initial data from:', this.firebaseEndpoint);
        await this.forceSync();
        return;
      }

      // If cache exists, check if stale (background update)
      const lastSync = await this.wikiDB.getLastSyncTime();
      const needsSync = await this.wikiDB.needsSync(this.syncIntervalMs);
      console.log('[WikiSync] Last sync:', new Date(lastSync).toISOString(), '- Needs sync:', needsSync);

      if (needsSync) {
        console.log('[WikiSync] Cache is stale, updating in background...');
        this.syncInBackground(); // Don't wait
        return;
      }

      console.log('[WikiSync] Cache is fresh, no sync needed');
    } catch (e) {
      console.error('[WikiSync] Error in syncIfNeeded:', e);
      console.error('[WikiSync] Stack:', e.stack);
      // Continue anyway - cache might still be available
    }
  }

  /**
   * Force sync: fetch from server and update cache
   */
  async forceSync() {
    console.log('[WikiSync] forceSync() called');

    if (this.syncing) {
      console.log('[WikiSync] Already syncing, waiting...');
      return;
    }

    this.syncing = true;

    try {
      console.log('[WikiSync] Starting forced sync...');
      console.log('[WikiSync] Updating sync status to "syncing"...');
      await this.wikiDB.updateSyncStatus('syncing');
      console.log('[WikiSync] Sync status updated');

      console.log('[WikiSync] Calling _fetchFromFirebase()...');
      const calculators = await this._fetchFromFirebase();
      console.log('[WikiSync] Fetched', calculators.length, 'calculators');

      if (calculators.length > 0) {
        console.log('[WikiSync] Sample calc:', JSON.stringify(calculators[0]).substring(0, 200));
        console.log('[WikiSync] Saving', calculators.length, 'calculators to IndexedDB...');
        await this.wikiDB.saveCalculators(calculators);
        console.log('[WikiSync] Save complete');
      } else {
        console.warn('[WikiSync] No calculators to save!');
      }

      console.log('[WikiSync] Updating last sync time...');
      await this.wikiDB.updateLastSyncTime(calculators.length);
      console.log('[WikiSync] Last sync time updated');

      console.log('[WikiSync] Updating sync status to "complete"...');
      await this.wikiDB.updateSyncStatus('complete');
      console.log('[WikiSync] Sync status updated');

      console.log('[WikiSync] ✓ SYNC COMPLETE. Total cached:', calculators.length);
    } catch (e) {
      console.error('[WikiSync] ✗ SYNC FAILED:', e.message);
      console.error('[WikiSync] Error:', e);
      try {
        await this.wikiDB.updateSyncStatus('error', e.message);
      } catch (statusErr) {
        console.error('[WikiSync] Failed to update error status:', statusErr);
      }
      throw e;
    } finally {
      this.syncing = false;
      console.log('[WikiSync] forceSync() finished. Syncing flag:', this.syncing);
    }
  }

  /**
   * Sync in background (non-blocking)
   */
  syncInBackground() {
    // Run sync but don't wait for it
    this._syncInBackground().catch(e => {
      console.error('[WikiSync] Background sync failed:', e);
    });
  }

  /**
   * Internal async background sync
   */
  async _syncInBackground() {
    if (this.syncing) {
      console.log('[WikiSync] Already syncing, skipping background update');
      return;
    }

    this.syncing = true;

    try {
      console.log('[WikiSync] Background sync started');
      await this.wikiDB.updateSyncStatus('syncing');

      const lastSync = await this.wikiDB.getLastSyncTime();
      const calculators = await this._fetchFromFirebase(lastSync);

      if (calculators.length > 0) {
        await this.wikiDB.saveCalculators(calculators);
        console.log('[WikiSync] Background sync: updated', calculators.length, 'calculators');
      } else {
        console.log('[WikiSync] Background sync: no updates needed');
      }

      await this.wikiDB.updateLastSyncTime(calculators.length);
      await this.wikiDB.updateSyncStatus('complete');

      console.log('[WikiSync] Background sync complete');
    } catch (e) {
      console.warn('[WikiSync] Background sync error (non-critical):', e);
      // Don't throw - we don't want background errors to break anything
    } finally {
      this.syncing = false;
    }
  }

  /**
   * Start periodic background sync
   * Runs every 5 minutes (or custom interval)
   */
  startBackgroundSync(intervalMs = null) {
    if (intervalMs) {
      this.syncIntervalMs = intervalMs;
    }

    if (this.syncInterval) {
      console.log('[WikiSync] Background sync already running');
      return;
    }

    console.log('[WikiSync] Starting periodic sync every', this.syncIntervalMs, 'ms');

    this.syncInterval = setInterval(() => {
      console.log('[WikiSync] Periodic sync triggered');
      this.syncInBackground();
    }, this.syncIntervalMs);
  }

  /**
   * Stop periodic sync
   */
  stopBackgroundSync() {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
      console.log('[WikiSync] Stopped periodic sync');
    }
  }

  // ============ PRIVATE METHODS ============

  /**
   * Fetch calculators from Firebase API
   * @param {number} sinceTimestamp - Only fetch calcs updated after this timestamp
   */
  async _fetchFromFirebase(sinceTimestamp = 0) {
    try {
      // For full sync (sinceTimestamp = 0), try static blob first
      if (sinceTimestamp === 0) {
        try {
          const blobCalcs = await this._fetchStaticBlob();
          if (blobCalcs && blobCalcs.length > 0) {
            console.log('[WikiSync] Loaded', blobCalcs.length, 'calcs from static blob');
            const transformed = blobCalcs.map(calc => this._validateCalc(calc));
            // Skip hydration for blob data - it already has full rows
            console.log('[WikiSync] Blob data has full rows, skipping hydration');
            return transformed;
          }
        } catch (blobError) {
          console.warn('[WikiSync] Static blob fetch failed, falling back to API:', blobError.message);
        }
      }

      // Fallback to existing API for incremental sync or if blob fails
      return this._fetchFromAPI(sinceTimestamp);
    } catch (e) {
      console.error('[WikiSync] FETCH ERROR:', e.message);
      console.error('[WikiSync] Error type:', e.constructor.name);
      console.error('[WikiSync] Error stack:', e.stack);
      throw e;
    }
  }

  /**
   * Fetch static wiki calcs blob (CDN-cached, gzipped)
   * Returns array of calculators with full rows
   */
  async _fetchStaticBlob() {
    const url = '/wiki-calcs.json.gz';
    console.log('[WikiSync] Fetching static blob from:', url);

    const startTime = performance.now();
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Blob fetch failed: ${response.status} ${response.statusText}`);
    }

    // Response is gzip-compressed JSON, browser handles decompression via Content-Encoding
    const data = await response.json();

    const fetchTime = performance.now() - startTime;
    console.log('[WikiSync] Static blob loaded in', fetchTime.toFixed(2), 'ms');
    console.log('[WikiSync] Blob version:', data.version, '- Generated:', new Date(data.generated).toISOString());
    console.log('[WikiSync] Blob contains', data.count, 'calculators');

    return data.calcs || [];
  }

  /**
   * Fetch from API (original method, used as fallback)
   */
  async _fetchFromAPI(sinceTimestamp = 0) {
    // Build API URL
    let url = `${this.firebaseEndpoint}/listRecent?limit=500`;
    if (sinceTimestamp > 0) {
      url += `&since=${sinceTimestamp}`;
    }

    console.log('[WikiSync] Starting API request...');
    console.log('[WikiSync] URL:', url);

    const startTime = performance.now();
    const response = await fetch(url);

    const fetchTime = performance.now() - startTime;
    console.log('[WikiSync] API fetch completed in', fetchTime.toFixed(2), 'ms');
    console.log('[WikiSync] Response status:', response.status);

    if (!response.ok) {
      throw new Error(`API error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();

    // API returns { calcs: [...] } or array directly
    const calculators = Array.isArray(data) ? data : data.calcs || [];
    console.log('[WikiSync] API returned', calculators.length, 'calculators');

    const transformed = calculators
      .map(calc => this._validateCalc(calc))
      .filter(calc => calc !== null);
      
    await this._hydrateCalculators(transformed);

    return transformed;
  }

  /**
   * Validate and normalize calculator object from API
   */
  _validateCalc(calc) {
    const id = calc.id || calc.slug || 'unknown';
    const slug = calc.slug || calc.id || 'unknown';
    
    // Ignore test artifacts
    if (id.startsWith('test-existing-') || slug.startsWith('test-existing-')) {
      return null;
    }

    // Parse timestamp from API response
    let updatedAt = Date.now();
    if (calc.updated) {
      updatedAt = new Date(calc.updated).getTime();
    } else if (calc.updatedAt) {
      updatedAt = typeof calc.updatedAt === 'string' ? new Date(calc.updatedAt).getTime() : calc.updatedAt;
    }

    // Generate previewRows for gallery card display (first 4 meaningful rows)
    const previewRows = this._generatePreviewRows(calc.rows || []);

    // Ensure required fields exist
    return {
      id: calc.id || calc.slug || 'unknown',
      slug: calc.slug || calc.id || 'unknown',
      title: calc.name || calc.title || 'Untitled',
      name: calc.name || calc.title || 'Untitled',
      description: calc.description || '',
      category: calc.category || (calc.tags && calc.tags.length > 0 ? calc.tags[0] : 'general'),
      tags: calc.tags || [],
      content: calc.content || '',
      rows: calc.rows || [],
      previewRows,
      stats: {
        views: calc.views || calc.stats?.views || 0,
        likes: calc.likes || calc.stats?.likes || 0
      },
      updatedAt,
      createdAt: calc.createdAt ?
        (typeof calc.createdAt === 'string' ? new Date(calc.createdAt).getTime() : calc.createdAt)
        : updatedAt,
      syncedAt: Date.now()
    };
  }

  /**
   * Generate preview rows for gallery card display
   * Extracts label/value pairs from calc rows for compact preview
   */
  _generatePreviewRows(rows) {
    if (!Array.isArray(rows) || rows.length === 0) return [];

    const previews = [];
    for (const row of rows) {
      if (previews.length >= 4) break;
      if (!row || !row.text) continue;

      const text = row.text.trim();
      // Skip empty rows, comments, and headers
      if (!text || text.startsWith('//') || text.startsWith('#')) continue;

      // Parse "label = value" or "label: value" patterns
      const match = text.match(/^([a-zA-Z_][a-zA-Z0-9_]*)\s*[=:]\s*(.+)$/);
      if (match) {
        previews.push({
          label: match[1].replace(/_/g, ' '),
          value: match[2].trim(),
          isResult: match[2].includes('=') || /^\$?[\d,.]+%?$/.test(match[2].trim())
        });
      }
    }
    return previews;
  }

  /**
   * Ensure calculators have full rows/content by calling wiki create API when needed
   * Only hydrate if:
   * 1. Calculator doesn't exist locally, OR
   * 2. Local calculator has no valid rows, OR
   * 3. Remote calculator is newer than local version
   */
  async _hydrateCalculators(calculators) {
    let calculatorsChecked = 0;
    const needsHydration = [];

    for (const calc of calculators) {
      if (!calc) continue;

      calculatorsChecked++;

      // Check if calculator has valid rows in the response
      const rows = calc.rows;
      const hasValidRows = Array.isArray(rows) && rows.length > 0 && rows.some(r => r && Object.keys(r).length > 0 && (r.text || r.value || r.comment));

      if (hasValidRows) {
        // Already has rows, no need to hydrate
        continue;
      }

      // Get local version to compare timestamps
      const localCalc = await this.wikiDB.getCalcById(calc.id);

      // Check if local has valid rows
      const localHasValidRows = localCalc && Array.isArray(localCalc.rows) &&
        localCalc.rows.length > 0 &&
        localCalc.rows.some(r => r && Object.keys(r).length > 0 && (r.text || r.value || r.comment));

      if (localHasValidRows) {
        // Local exists with valid rows - only hydrate if remote is newer
        if (localCalc.updatedAt && calc.updatedAt && calc.updatedAt <= localCalc.updatedAt) {
          // Remote is not newer, skip hydration
          continue;
        }
      } else if (localCalc) {
        // Local exists but has no valid rows.
        // If we've already attempted hydration for this version, skip to avoid loops.
        const localUpdatedAt = localCalc.updatedAt || 0;
        const remoteUpdatedAt = calc.updatedAt || 0;
        if (localCalc.hydrationAttemptedAt && localUpdatedAt >= remoteUpdatedAt) {
          continue;
        }
        // Otherwise, add to needsHydration below.
      }

      // Either local doesn't exist, local is outdated, or local has no rows - needs hydration
      needsHydration.push(calc);
    }

    if (needsHydration.length === 0) {
      console.log('[WikiSync] Checked', calculatorsChecked, 'calculators; all have up-to-date rows; hydration skipped');
      return;
    }

    console.log('[WikiSync] Checked', calculatorsChecked, 'calculators; hydrating', needsHydration.length, 'with full rows (', Math.round((needsHydration.length / calculatorsChecked) * 100), '% need update)');

    const concurrency = 5;
    let index = 0;
    const errors = [];

    const worker = async () => {
      while (index < needsHydration.length) {
        const currentIndex = index++;
        const calc = needsHydration[currentIndex];
        if (!calc) continue;

        const identifier = calc.slug || calc.id || calc.title || calc.name;
        if (!identifier) {
          console.warn('[WikiSync] Skipping hydration for calc without identifier:', calc);
          continue;
        }

        try {
          const fullCalc = await this._fetchFullCalc(identifier, calc.id);
          calc.hydrationAttemptedAt = Date.now();
          if (fullCalc && Array.isArray(fullCalc.rows) && fullCalc.rows.length > 0) {
            calc.rows = fullCalc.rows;
            calc.content = fullCalc.content || calc.content;
            calc.description = calc.description || fullCalc.description || '';
            calc.tags = (fullCalc.tags && fullCalc.tags.length > 0) ? fullCalc.tags : calc.tags;
            calc.category = calc.category || (calc.tags && calc.tags.length > 0 ? calc.tags[0] : fullCalc.category || 'general');
            calc.stats = {
              views: fullCalc.views ?? fullCalc.stats?.views ?? calc.stats?.views ?? 0,
              likes: fullCalc.likes ?? fullCalc.stats?.likes ?? calc.stats?.likes ?? 0
            };
            if (fullCalc.updated) {
              calc.updatedAt = new Date(fullCalc.updated).getTime();
            }
            if (fullCalc.created) {
              calc.createdAt = new Date(fullCalc.created).getTime();
            }
          }
        } catch (e) {
          calc.hydrationAttemptedAt = Date.now();
          console.warn('[WikiSync] Failed to hydrate calc', identifier, e);
          errors.push({ identifier, error: e });
        }
      }
    };

    const workers = Array.from({ length: Math.min(concurrency, needsHydration.length) }, () => worker());
    await Promise.all(workers);

    if (errors.length > 0) {
      console.warn('[WikiSync] Hydration completed with', errors.length, 'errors');
    } else {
      console.log('[WikiSync] Hydration complete for all calculators');
    }
  }

  /**
   * Fetch full calculator data (including rows/content)
   */
  async _fetchFullCalc(slugOrId, fallbackId = null) {
    const slug = typeof slugOrId === 'string' ? slugOrId : (slugOrId?.slug || slugOrId?.id || slugOrId?.name || slugOrId?.title);
    if (!slug) {
      throw new Error('Missing slug/id for full calc fetch');
    }

    const body = {
      slug,
      name: slug,
      rows: []
    };

    if (fallbackId && fallbackId !== slug) {
      body.id = fallbackId;
    }

    const url = `${this.firebaseEndpoint}/create`;
    const start = performance.now();
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });

    const duration = performance.now() - start;
    console.log('[WikiSync] Fetch full calc', slug, 'status:', response.status, 'in', duration.toFixed(2), 'ms');

    if (!response.ok) {
      throw new Error(`Full calc fetch failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data.calc || data;
  }

  /**
   * Get current sync status for UI display
   */
  async getStatus() {
    const status = await this.wikiDB.getSyncStatus();
    const count = await this.wikiDB.getCount();
    const lastSync = await this.wikiDB.getLastSyncTime();

    return {
      status: status.status || 'unknown',
      syncing: this.syncing,
      count,
      lastSync,
      lastSyncTime: new Date(lastSync).toLocaleString(),
      progress: status.progress || { current: 0, total: 0 }
    };
  }
}

// Make WikiSync available globally for browser usage
window.WikiSync = WikiSync;
